<template>
  <b-modal
    id="modal-batch-list"
    ref="modalBatchList"
    title="Select Batch for Cancellation"
    size="xl"
    hide-footer
    @shown="fetchBatches"
    @hidden="resetModal">
    <div>
      <!-- Course Info Header -->
      <div class="mb-4">
        <div class="row">
          <div class="col-6">
            <h5 class="mb-2">{{ selectedCourse?.title }}</h5>
            <p class="text-muted mb-0">{{ selectedCourse?.description }}</p>
          </div>
          <div class="col-6 d-flex justify-content-end align-items-start gap-2">
            <button 
              class="btn btn-sm btn-outline-info"
              @click="downloadTemplate">
              <i class="fe fe-download me-1"></i>
              Download Template
            </button>
            <button 
              class="btn btn-sm btn-success"
              @click="showBulkUploadModal">
              <i class="fe fe-upload me-1"></i>
              Upload Excel
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="batchLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading batches...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="batches.length === 0" class="text-center py-5">
        <i class="fe fe-inbox" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">No batches available</p>
      </div>

      <!-- Batch Table -->
      <div v-else>
        <vue-good-table
          :columns="batchColumns"
          :rows="batches"
          :pagination-options="{ enabled: true }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'batch'">
              {{ formatBatchLabel(props.row) }}
            </span>
            <span v-else-if="props.column.field === 'penyelenggara'">
              {{ formatPenyelenggara(props.row.penyelenggara) }}
            </span>
            <span v-else-if="props.column.field === 'batch_status'">
              <span class="badge" :class="getBatchStatusClass(props.row.batch_status)">
                {{ props.row.batch_status }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'action'">
              <button class="btn btn-sm btn-outline-primary" @click="selectBatch(props.row, props.index)">
                <i class="fe fe-users me-1"></i> View
              </button>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>

    <!-- Bulk Upload Modal -->
    <modal-bulk-upload 
      :selectedCourse="selectedCourse"
      @upload-success="handleUploadSuccess"
    />
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import ModalBulkUpload from './modal-bulk-upload.vue';
import { formatBatchLabel, formatPenyelenggara } from '@/utils/batchIdentity';

export default {
  name: 'ModalBatchList',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    selectedPeriod: {
      type: [Number, String],
      default: null
    }
  },
  components: {
    VueGoodTable,
    ModalBulkUpload
  },
  computed: {
    ...mapGetters('mitraUnenrollment', ['batches', 'batchLoading'])
  },
  data() {
    return {
      batchColumns: [
        { label: 'Batch', field: 'batch' },
        { label: 'Penyelenggara', field: 'penyelenggara', width: '180px' },
        { label: 'Status', field: 'batch_status' },
        { label: 'Quota', field: 'quota' },
        { label: 'Enrolled', field: 'enrolled' },
        { label: 'Available', field: 'available_quota' },
        { label: 'Participants', field: 'action', width: '150px' }
      ]
    };
  },
  methods: {
    formatBatchLabel,
    formatPenyelenggara,
    async fetchBatches() {
      console.log('🔍 Fetching batches for course:', this.selectedCourse);
      console.log('🔍 Selected Period:', this.selectedPeriod);
      
      const courseId = this.selectedCourse?.training_event_course_id || this.selectedCourse?.id;
      
      if (!courseId) {
        console.error('❌ No course ID found');
        return;
      }
      
      console.log('✅ Using course ID:', courseId);
      
      try {
        const params = {};
        if (this.selectedPeriod) {
          params.period = this.selectedPeriod;
        }
        
        console.log('📤 Dispatching getBatches with:', { courseId, params });
        
        await this.$store.dispatch('mitraUnenrollment/getBatches', {
          courseId,
          params
        });
        
        console.log('✅ Batches fetched successfully');
      } catch (error) {
        console.error('❌ Error fetching batches:', error);
        this.$swal({
          title: 'Error',
          text: error.message || 'Daftar batch belum berhasil dimuat. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    resetModal() {
      // Reset any local state if needed
    },
    
    selectBatch(batch, index) {
      this.$emit('batch-selected', { batch, index });
    },
    
    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    },

    async downloadTemplate() {
      const courseId = this.selectedCourse?.id;
      
      if (!courseId) {
        this.$swal({
          title: 'Error',
          text: 'ID pelatihan tidak ditemukan',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return;
      }
      
      try {
        const result = await this.$store.dispatch('mitraUnenrollment/downloadTemplate', courseId);

        // Create blob and download
        const blob = new Blob([result.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // link.download = result.filename;
        link.setAttribute('download', `unenrollment_${courseId}_template.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.$swal({
          title: 'Success',
          text: 'Template berhasil diunduh',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#00c9a7'
        });
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$swal({
          title: 'Error',
          text: error.message || 'Template belum berhasil diunduh. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },

    showBulkUploadModal() {
      this.$bvModal.show('modal-bulk-upload');
    },

    handleUploadSuccess() {
      // Refresh batches after successful upload
      this.fetchBatches();
    }
  }
};
</script>

<style scoped>
.me-1 {
  margin-right: 0.25rem;
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}
</style>
